/**
 * Anmeldung, Passwort-Wiederherstellung und das Setzen des ersten Passworts.
 *
 * Ein Passwort festlegen geht über zwei Links: den Einladungslink aus der
 * Funktion `admin` (`?invite=…&token=…`, sieben Tage gültig) und den
 * Wiederherstellungslink von Appwrite (`?userId=…&secret=…`, eine Stunde).
 * Beide landen in derselben Karte.
 *
 * Die drei Zustände liegen in einer Datei, weil sie dieselbe Karte, dieselbe
 * Fehlerbehandlung und dieselben Übergänge teilen.
 */

import { useState, type FormEvent } from 'react';
import { de } from '@zeit/domain';
import { ApiError, auth } from '../lib/appwrite.ts';
import { ErrorText, Field, PasswordInput } from '../ui/index.tsx';

const a = de.auth;

type Mode = 'login' | 'recover' | 'reset';

type LinkParams =
  | { kind: 'invite'; userId: string; token: string }
  | { kind: 'recovery'; userId: string; secret: string };

function linkParams(): LinkParams | null {
  const q = new URLSearchParams(window.location.search);
  const invite = q.get('invite');
  const token = q.get('token');
  if (invite && token) return { kind: 'invite', userId: invite, token };
  // Appwrite hängt `userId` und `secret` an den Wiederherstellungslink.
  const userId = q.get('userId');
  const secret = q.get('secret');
  return userId && secret ? { kind: 'recovery', userId, secret } : null;
}

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [mode, setMode] = useState<Mode>(() => (linkParams() ? 'reset' : 'login'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * Wechselt die Karte und leert alte Meldungen.
   *
   * Bewusst kein Effekt auf `mode`: der lief nach dem Wechsel und löschte
   * damit auch die Meldung, die zum Wechsel gehört („Passwort gespeichert“).
   */
  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    setInfo('');
    setPassword('');
    setRepeat('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');

    try {
      if (mode === 'login') {
        await auth.login(email.trim().toLowerCase(), password);
        onSignedIn();
        return;
      }

      if (mode === 'recover') {
        const url = `${window.location.origin}${window.location.pathname}`;
        // Immer dieselbe Bestätigung, ob die Adresse existiert oder nicht —
        // sonst wird das Formular zum Verzeichnis der Belegschaft. Appwrite
        // antwortet auf eine unbekannte Adresse mit 404; die geht deshalb
        // als Erfolg durch statt als „User … could not be found“.
        await auth.recover(email.trim().toLowerCase(), url).catch((err) => {
          if ((err as { code?: number }).code !== 404) throw err;
        });
        setInfo(a.recoverSent);
        return;
      }

      const params = linkParams();
      if (!params) {
        setError(de.error.generic);
        return;
      }
      if (password.length < 8) {
        setError(a.passwordTooShort);
        return;
      }
      if (password !== repeat) {
        setError(a.passwordMismatch);
        return;
      }
      if (params.kind === 'invite') {
        let signIn: string;
        try {
          ({ email: signIn } = await auth.acceptInvite(params.userId, params.token, password));
        } catch (err) {
          if (!(err instanceof ApiError) || err.code !== 'invite_invalid') throw err;
          window.history.replaceState({}, '', window.location.pathname);
          switchMode('recover');
          setError(a.inviteExpired);
          return;
        }
        window.history.replaceState({}, '', window.location.pathname);
        // Wer gerade sein Passwort festgelegt hat, soll es nicht gleich noch
        // einmal eintippen müssen.
        await auth.login(signIn, password);
        onSignedIn();
        return;
      }

      try {
        await auth.completeRecovery(params.userId, params.secret, password);
      } catch (err) {
        // Appwrite lässt den Link nach einer Stunde fest verfallen (nicht
        // einstellbar), und nach dem ersten Benutzen ist er verbraucht. Dann
        // kam bisher „Invalid token passed in the request.“ und kein Weg
        // weiter — jetzt geht es direkt zum Anfordern eines neuen Links.
        const code = (err as { code?: number }).code;
        if (code !== 401 && code !== 404) throw err;
        window.history.replaceState({}, '', window.location.pathname);
        switchMode('recover');
        setError(a.linkExpired);
        return;
      }
      // Der Link ist verbraucht; ohne Aufräumen bliebe er im Verlauf stehen.
      window.history.replaceState({}, '', window.location.pathname);
      switchMode('login');
      setInfo(a.setPasswordDone);
    } catch (err) {
      const message = err instanceof ApiError || err instanceof Error ? err.message : '';
      // Nur eine abgelehnte Anmeldung (401) ist ein falsches Passwort. Fiel
      // der Server aus, stand hier vorher trotzdem „E-Mail oder Passwort
      // stimmt nicht“ — und die Belegschaft suchte den Fehler bei sich.
      const rejected = (err as { code?: number }).code === 401;
      setError(mode === 'login' && rejected ? a.invalid : message || de.error.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <form className="auth-card" onSubmit={submit}>
        <div className="brand" style={{ marginBottom: 4 }}>
          {de.app.name}
        </div>
        <div className="kicker" style={{ marginBottom: 24 }}>
          {de.app.tagline}
        </div>

        <h4 style={{ marginBottom: 6 }}>
          {mode === 'login' ? a.title : mode === 'recover' ? a.recoverTitle : a.setPasswordTitle}
        </h4>
        <p className="text-muted" style={{ fontSize: 13 }}>
          {mode === 'login' ? a.intro : mode === 'recover' ? a.recoverIntro : ''}
        </p>

        <div style={{ display: 'grid', gap: 14, marginTop: 18 }}>
          {mode !== 'reset' && (
            <Field label={a.email} htmlFor="email">
              <input
                id="email"
                className="input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ minHeight: 44 }}
              />
            </Field>
          )}

          {mode !== 'recover' && (
            <Field label={a.password} htmlFor="password">
              <PasswordInput
                id="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ minHeight: 44 }}
              />
            </Field>
          )}

          {mode === 'reset' && (
            <Field label={a.passwordRepeat} htmlFor="repeat">
              <PasswordInput
                id="repeat"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                style={{ minHeight: 44 }}
              />
            </Field>
          )}

          <ErrorText>{error}</ErrorText>
          {info && (
            <div
              style={{
                padding: '10px 12px',
                background: 'var(--color-on-bg)',
                color: 'var(--color-on-text)',
                fontSize: 13,
              }}
            >
              {info}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy}
            style={{ minHeight: 48, fontSize: 15 }}
          >
            {mode === 'login' ? a.submit : mode === 'recover' ? a.recoverSubmit : a.setPasswordSubmit}
          </button>

          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => switchMode(mode === 'login' ? 'recover' : 'login')}
            style={{ justifySelf: 'start' }}
          >
            {mode === 'login' ? a.forgot : a.backToLogin}
          </button>
        </div>
      </form>
    </div>
  );
}
